<script>
import { get, find, omit, has, last, isEmpty, size, isNil, first, set } from 'lodash';

import { validatePhoneE164 } from 'helpers/validation-helper';

import NuttremaLayoutFormStandard from 'components/ui/layout/nuttrema-layout-form-standard';
import FormStandardBehavior from 'src/components/mixins/form-standard-behavior';
import NuttremaFormAlert from 'components/ui/alert/nuttrema-form-alert';

import { SUBSCRIPTION_PLAN_TYPE, TAX, PAYMENT_METHOD } from 'constants/values';
import { Card, createSource } from 'vue-stripe-elements-plus';

export default {
  name: 'cmpt-subscription-plan-review-abstract',
  mixins: [
    FormStandardBehavior,
  ],
  props: {
    SubscriptionReview: {
      required: true,
      default: null,
    },
    RegionalAndLanguageAll: {
      required: true,
      default: null,
    },
  },
  components: {
    Card,
    NuttremaFormAlert,
    NuttremaLayoutFormStandard,
  },
  data() {
    return {
      SUBSCRIPTION_PLAN_TYPE,
      PAYMENT_METHOD,
      processing: false,
      stripe: {
        focus: false,
        complete: false,
        key: process.env.STRIPE_KEY,
        options: {
          hidePostalCode: true,
          style: {
            base: {
              fontSize: '14px',
              lineHeight: '24px',
            },
          },
        },
      },
      formData: {
        card: null,
        email: null,
        phone: null,
        region_id: null,
        owner_name: null,
        stripe_source_id: null,
        auto_renewal: true, // 1
        payment_source_id: null, // 1
        payment_method: PAYMENT_METHOD.STRIPE_SOURCE,
        PersonData: {
          address_street: {
            section: 'address',
            key: 'street',
            value: null,
          },
          address_house_no: {
            section: 'address',
            key: 'house_no',
            value: null,
          },
          address_city: {
            section: 'address',
            key: 'city',
            value: null,
          },
          address_zip: {
            section: 'address',
            key: 'zip',
            value: null,
          },
          licence_professional_id: {
            section: 'licence',
            key: 'id',
            value: null,
          },
        },
        InstanceData: {
          company_information_vat_number: {
            section: 'company_information',
            key: 'vat_number',
            value: null,
          },
        },
      },
      rules: {},
    };
  },
  computed: {
    isTrial() {
      return get(this.SubscriptionReview, 'data.SubscribedPlan.SubscriptionPlanType.code') === SUBSCRIPTION_PLAN_TYPE.TRIAL;
    },
    taxRate() {
      const region = this.getSelectedRegion();
      const membership = get(region, 'membership');
      return membership ? TAX.EU : 0;
    },
  },
  methods: {
    onPaymentMethodChange() {
      // Set saved card to null
      this.formData.payment_source_id = null;

      this.clearValidate();
      this.createRules();
    },
    validatePhone(rule, value, callback) {
      if (!validatePhoneE164(value)) return callback(new Error());
      return callback();
    },
    createRules() {
      const rules = {};

      set(rules, 'email', [
        { required: true,
          trigger: 'blur',
          message: this.formatValidationMessage('validation.required', 'label.email', true),
        },
        { type: 'email',
          trigger: 'blur',
          message: this.formatValidationMessage('validation.invalid', 'label.email', true),
        },
      ]);

      set(rules, 'phone', [
        {
          required: true,
          trigger: 'blur',
          message: this.formatValidationMessage('validation.required', 'label.phone', true),
        },
        {
          trigger: 'blur',
          validator: this.validatePhone,
          message: this.formatValidationMessage('validation.invalid', 'label.phone_format', true),
        },
      ]);

      set(rules, 'owner_name', [
        {
          required: true,
          trigger: 'blur',
          message: this.formatValidationMessage('validation.required', 'label.owner_name', true),
        },
      ]);

      if (!this.isTrial) {
        set(rules, 'region_id', [
          {
            type: 'array',
            required: true,
            trigger: 'blur',
            message: this.formatValidationMessage('validation.required', 'label.country', true),
          },
        ]);

        // Bank transfer
        if (this.formData.payment_method === PAYMENT_METHOD.STRIPE_SOURCE) {
          set(rules, 'PersonData.address_zip.value', [
            {
              required: true,
              trigger: 'blur',
              message: this.formatValidationMessage('validation.required', 'label.zip', true),
            },
          ]);

          set(rules, 'card', [
            {
              required: true,
              trigger: 'change',
              message: this.formatValidationMessage('validation.required', 'label.card', true),
            },
          ]);
        }
        // if stripe source
      }
      // if not trial

      this.rules = rules;
    },
    getPlaceholderText(placeholder) {
      return this.isTrial ? `* ${placeholder}` : placeholder;
    },
    stripeCardFocus() {
      this.stripe.focus = true;
    },
    stripeCardBlur() {
      this.stripe.focus = false;
      if (!this.isTrial) {
        if (this.$refs[this.formRef]) this.$refs[this.formRef].validateField('card');
      }
    },
    getSelectedRegion() {
      let region = null;

      const selectedRegion = get(this.formData, 'region_id', []);
      const regions = get(this.RegionalAndLanguageAll, 'data.Regions', []);

      if (!isEmpty(selectedRegion) && size(selectedRegion) === 2) {
        const parentRegion = find(regions, item => Number(item.id) === Number(selectedRegion[0]));

        if (parentRegion) {
          const childRegion = find(parentRegion.SubRegions, item => Number(item.id) === Number(selectedRegion[1]));
          if (childRegion) {
            region = {
              code: get(childRegion, 'code', null),
              membership: first(get(childRegion, 'membership', [])),
            };
          }
        }
      }

      return region;
    },
    previousStep() {
      this.$emit('previousStep');
    },
    proceedWithPayment() {
      let validated = false;

      this.$refs[this.formRef].validate((valid) => {
        console.log('in validion');
        validated = valid;
        if (valid) {
          if (this.isTrial && isNil(this.formData.card)) this.registerTrial();
          // Validate trial also
          else if (this.formData.payment_method === PAYMENT_METHOD.BANK_TRANSFER) {
            this.checkoutBankTransfer();
          } else if (this.formData.payment_method === PAYMENT_METHOD.SAVED_SOURCE) {
            this.checkoutSavedSource();
          } else {
            this.checkoutNewSource();
          }
        }
      });

      console.log('gate 2');

      return validated;
    },
    registerTrial() {
      this.processing = true;
      const payload = this.getFormData();
      this.$emit('checkout', payload);
    },
    checkoutBankTransfer() {
      this.processing = true;
      const payload = this.getFormData();

      // disable auto_renewal for bank transfrer
      set(payload, 'auto_renewal', false);
      this.$emit('checkout', payload);
    },
    checkoutSavedSource() {
      this.processing = true;
      const payload = this.getFormData();
      this.$emit('checkout', payload);
    },
    checkoutNewSource() {
      this.processing = true;

      const name = this.formData.owner_name;
      const email = this.formData.email;

      const line1 = `${get(this.formData, 'PersonData.address_street.value', '')} ${get(this.formData, 'PersonData.address_house_no.value', '')}`;
      const city = get(this.formData, 'PersonData.address_city.value', '');
      const postalCode = get(this.formData, 'PersonData.address_zip.value', '');
      const region = this.getSelectedRegion();
      const country = get(region, 'code');

      const info = {
        owner: {
          name,
          email,
          address: {
            line1,
            city,
            postal_code: postalCode,
            country,
          },
        },
      };

      createSource(info).then((result) => {
        if (result.error) {
          console.log(result.error.message);
          this.processing = false;
        } else {
          console.log('createSource result', result);
          this.formData.stripe_source_id = get(result, 'source.id', null);

          const payload = this.getFormData();
          this.$emit('checkout', payload);
        }
      });
    },
    creditCardChanged(evt) {
      console.log(evt);
      this.stripe.complete = evt.complete;
      this.formData.card = evt.complete ? 'card-added' : null;
    },
    setFormData() {
      const selectedPaymentPlan = find(get(this.SubscriptionReview, 'data.PaymentPlans', []), item => item.selected);
      this.formData.payment_plan_id = get(selectedPaymentPlan, 'id', null);

      const paymentSources = get(this.SubscriptionReview, 'data.Person.PaymentSources', []);
      if (paymentSources.length > 0) {
        this.formData.payment_method = PAYMENT_METHOD.SAVED_SOURCE;
        const savedSource = first(paymentSources);
        if (savedSource) this.formData.payment_source_id = get(savedSource, 'id');
      }

      const person = get(this.SubscriptionReview, 'data.Person');
      if (person) {
        this.formData.owner_name = get(person, 'full_name', null);
        this.formData.region_id = get(person, 'region_id', null);
        this.formData.PersonData.address_street.value = get(person, 'PersonData.address_street.value', null);
        this.formData.PersonData.address_house_no.value = get(person, 'PersonData.address_house_no.value', null);
        this.formData.PersonData.address_city.value = get(person, 'PersonData.address_city.value', null);
        this.formData.PersonData.address_zip.value = get(person, 'PersonData.address_zip.value', null);
      }

      const instanceData = get(this.SubscriptionReview, 'data.InstanceData');
      if (instanceData) {
        this.formData.InstanceData.company_information_vat_number.value = get(instanceData, 'InstanceData.company_information_vat_number.value', null);
      }
    },
    getFormData() {
      const data = omit(this.formData, ['card']);
      if (has(data, 'region_id')) data.region_id = last(data.region_id);
      return this.removeEmpty(data);
    },
  },
  created() {
    this.setFormData();
    this.createRules();
    this.formRef = null;
  },
};
</script>



// WEBPACK FOOTER //
// src/components/ui/subscription/cmpt-subscription-plan-review-abstract.vue